<template>
	<div class="MultiselectMobile">
		<button
			class="BackButton"
			aria-label="Close Multiselect"
			@click="$emit('close')"
		>
			<i class="lnr-chevron-left" aria-hidden="true"></i>
		</button>

		<VueFormMultiselect
			ref="multiselectRef"
			:key="filterKey"
			v-model="multiselectValue"
			:mode="props.mode"
			:options="visibleOptions"
			:placeholder="props.placeholder"
			:searchable="props.searchable"
			:close-on-select="false"
			:hide-selected="props.mode === MultiselectMode.SINGLE"
			:caret="false"
			:open-direction="'bottom'"
			:no-results-text="props.noResultsText"
			:loading="props.loading"
			class="Multiselect isAlwaysOpen"
			:class="{ 'isModalOpen': modalState.isOpen }"
			:clear-on-blur="false"
			@search-change="handleSearchChange"
		>
			<template #nooptions>
				<p class="MultiselectNoOptions" :class="{ 'isLoading': props.loading }">
					<span v-if="props.loading" class="multiselect-spinner" aria-hidden="true"></span>
					<span v-if="props.loading">Searching...</span>
					<span v-else>{{ showOptions ? props.noOptionsText : props.startTypingText }}</span>
				</p>
			</template>
			<template #option="{ option, isSelected }">
				<div class="SelectItemWrapper">
					<SelectItem
						:item="mapOptionToSelectItem(option, isSelected(option))"
						:type="mobileSelectItemType"
					>
						<template v-if="option.icon" #icon>
							<i :class="`Icon ${option.icon}`" aria-hidden="true"></i>
						</template>
					</SelectItem>
				</div>
			</template>

			<template #beforelist>
				<p v-if="props.description" class="MultiselectDescription">{{ props.description }}</p>
				<p v-if="props.warning" class="MultiselectWarning">{{ props.warning }}</p>

				<div v-if="props.selectedContinentName" class="ContinentChange">
					<span>Continent {{ props.selectedContinentName }}</span>
					<button type="button" class="ChangeButton" @click.stop.prevent="handleChangeContinentClick">
						Change
					</button>
				</div>
			</template>

			<template #singlelabel="{ value }">
				<div class="MultiselectSingleLabel" :class="{ isPlaceholder: props.alwaysShowPlaceholder }">
					<i v-if="value.icon" :class="`Icon ${value.icon}`" aria-hidden="true"></i>
					<span>{{ props.alwaysShowPlaceholder ? props.placeholder : value.label }}</span>
				</div>
			</template>

			<template v-if="props.mode === MultiselectMode.TAGS" #tag="{ option, handleTagRemove }">
				<Chip
					:key="option.value"
					:label="option.label"
					@remove="(e: Event) => handleTagRemove(option, e)"
				/>
			</template>

			<template #clear="{ clear }">
				<i
					class="ClearIcon lnr-cross"
					aria-hidden="true"
					@mousedown.prevent.stop="clear"
				></i>
			</template>
		</VueFormMultiselect>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onActivated, nextTick, defineAsyncComponent, computed, watch, ComponentPublicInstance } from 'vue';
const VueFormMultiselect = defineAsyncComponent(() => import('@vueform/multiselect'));
import '@vueform/multiselect/themes/default.css';
import SelectItem from '@components/SelectItem/SelectItem.vue';
import Chip from '@components/TagsElement/Chip.vue';
import { useMultiselect } from '@composables/useMultiselect';
import { multiselectPropsDefinition } from '@app/interfaces/useMultiselect.interface';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { SelectListItemType } from '@app/enums/SelectListItemType';
import ISelectItem from '@app/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { useMobileModal } from '@composables/useMobileModal';

const { modalState } = useMobileModal();
const props = defineProps(multiselectPropsDefinition);

const emit = defineEmits<{
	'updateValues': [value: string | number | (string | number)[] | null];
	'update:modelValue': [value: string | number | (string | number)[] | null];
	'open': [];
	'close': [];
	'changeContinent': [];
	'searchChange': [value: string];
}>();

const multiselectRef = ref<ComponentPublicInstance | null>(null);
const {
	multiselectValue,
	selectItemType,
	mapOptionToSelectItem,
	showOptions,
	visibleOptions,
	handleSearchChange,
	filterKey
} = useMultiselect({ props, emit });


const clearMultiselectValue = (): void => {
	if (props.mode === MultiselectMode.SINGLE) {
		multiselectValue.value = null;
	}
};

const isSelectItemOptions = (options: Array<IMultiselectOption | ISelectItem>): boolean =>
	options.some((option) => 'text' in option);

const mobileSelectItemType = computed(() => (
	isSelectItemOptions(props.options) ? SelectListItemType.RADIO : selectItemType.value
));

const handleChangeContinentClick = (): void => {
	emit('changeContinent');
};

const openDropdown = async (): Promise<void> => {
	await nextTick();
	const multiselect = multiselectRef.value as any;
	if (multiselect && !multiselect.isOpen) {
		multiselect.open();
	}
};

const setMultiselectInputHeight = (): void => {
	const multiselectEl = multiselectRef.value?.$el as HTMLElement;
	if (!multiselectEl) return;

	let inputHeight = multiselectEl.clientHeight;
	const mobileModalFooterOffset = 34;

	const areWeInAMobileModalWithAFooter = modalState.value.props.footer;
	if (areWeInAMobileModalWithAFooter) {
		inputHeight += mobileModalFooterOffset;
	}

	document.body.style.setProperty('--multiselect-input-height', `${inputHeight}px`);
};

onMounted(async () => {
	openDropdown();
});

onActivated(() => {
	clearMultiselectValue();
	openDropdown();
});

watch(multiselectRef, () => {
    setMultiselectInputHeight();
},{ flush: 'post', immediate: true });

watch(() => props.options, () => {
	openDropdown();
}, { deep: true });

watch(multiselectValue, (value) => {
	emit('updateValues', value);

	void nextTick(() => setMultiselectInputHeight());

	if (props.mode === MultiselectMode.SINGLE && value !== null && value !== undefined && !Array.isArray(value)) {
		emit('close');
	}
}, { deep: true });
</script>


<style lang="scss">
.MobileModal {
	background: $White !important;
}
</style>

<style scoped lang="scss">
.MultiselectMobile {
	display: flex;
	background: $White;
	align-items: center;
	@include PaddingAreas(1);

	.BackButton {
		height: 2rem;
		width: 2rem;
		cursor: pointer;
		border-radius: .25rem;
		border: 1px solid $Grey-200;
		background: $White;

		@include PaddingAreas(0.5);

		i {
			color: $Grey-900;
		}
	}

	:deep(.multiselect) {
		position: relative;
		min-height: auto;
		height: auto;
	}

	.Multiselect {
		width: 100%;
		border-radius: 0.25rem;

		--ms-font-size: 1rem;
		--ms-font-weight: 400;
		--ms-bg: #{$White};
		--ms-border-color: #{$Grey-300};
		--ms-border-width: 1px;
		--ms-radius: .25rem;
		--ms-ring-width: 0;
		--ms-ring-color: transparent;
		--ms-placeholder-color: #{$Grey-600};
		--ms-px: 0;

		// Dropdown - always visible in mobile modal
		--ms-dropdown-bg: #{$White};
		--ms-dropdown-radius: 0;

		// Options
		--ms-option-font-size: 1rem;
		--ms-option-line-height: 1rem;
		--ms-option-bg-pointed: transparent;
		--ms-option-color-pointed: #{$Grey-900};
		--ms-option-bg-selected: transparent;
		--ms-option-bg-selected-pointed: transparent;
		--ms-option-color-selected: #{$Grey-900};
		--ms-option-color-selected-pointed: #{$Grey-900};
		--ms-option-py: .25rem;
		--ms-option-px: .5rem;

		@include MarginAreas(0, 0, 0, 0.5);
		@include PaddingAreas(0, 0.5);

		.ContinentChange {
			display: flex;
			align-items: center;
			justify-content: space-between;
			color: $Grey-700;
			background: $Grey-100;
			border-top: 1px solid $Grey-200;
			border-bottom: 1px solid $Grey-200;

			@include TextStyle(UiLargeRegular);
			@include PaddingAreas(0.5, 1);

			.ChangeButton {
				color: $Blue-700;
				background: none;
				border: none;
				cursor: pointer;

				&:focus-visible {
					outline: 2px solid #{$Blue-600};
					border-radius: .25rem;
				}
			}
		}

		&.isAlwaysOpen {
			:deep(.multiselect-dropdown) {
				position: absolute;
				overflow: scroll;
				display: block !important;
				left: -2.5rem;
				max-height: calc(100vh - var(--multiselect-input-height) - 9rem);
				border: none;
				box-shadow: none;
				bottom: -1.5rem;
				margin: 0 -1rem;
			}
		}

		&:not(.is-open):not(.isModalOpen) {
			:deep(.multiselect-tags) {
				.Chip {
					display: none;
				}
			}
		}

		&.is-active {
			outline: 2px solid #{$Blue-600};
		}
	}

	.SelectItemWrapper {
		width: 100%;
		pointer-events: none;

		:deep(.SelectItem) {
			max-width: 100%;
			@include PaddingAreas(0.5);
		}
	}

	:deep(.multiselect-option) {
		@include PaddingAreas(0, 0.5);
		max-height: none !important;

		&:last-child {
			border-bottom: none;
		}
	}

	:deep(.multiselect-no-results) {
		color: $Grey-900;
		@include TextStyle(BodyMediumRegular);
		@include PaddingAreas(1, 1, 1, 3);
	}

	&:not(:has(.ContinentChange)) :deep(.multiselect-options) {
		border-top: 1px solid $Grey-300;
	}

	:deep(.multiselect-placeholder) {
		@include PaddingAreas(0.25);
	}

	.MultiselectSingleLabel {
		display: flex;
		align-items: center;
		gap: .5rem;
		position: absolute;
		top: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;

		.Icon {
			width: 1rem;
			height: 1rem;
			flex-shrink: 0;
		}

		&.isPlaceholder {
			color: $Grey-600;
		}
	}

	.ClearIcon {
		color: $Grey-600;
		cursor: pointer;
		@include MarginAreas(0, 0.5, 0, 0);

		&:hover {
			color: $Grey-900;
		}
	}

	:deep(.multiselect-tags) {
		gap: .5rem;
		flex-wrap: wrap;
		padding: .75rem 0;
		margin: 0;
	}

	:deep(.multiselect-tags-search-wrapper) {
		width: 100%;
	}

	.MultiselectDescription {
		color: $Grey-700;
		@include TextStyle(BodyMediumRegular);
		@include MarginAreas(0, 0, 0.25, 3);
	}

	.MultiselectWarning {
		color: $Red-500;
		@include TextStyle(BodyMediumRegular);
		@include MarginAreas(0, 0, 0.25, 3);
	}

	.MultiselectNoOptions {
		color: $Grey-900;
		@include TextStyle(BodyMediumRegular);
		@include PaddingAreas(1, 0, 0, 3);
		@include MarginAreas(0);

		&.isLoading {
			display: flex;
			align-items: center;
			gap: 0.5rem;
		}
	}
}
</style>
